import { useCallback, useEffect, useRef } from 'react';

type Props = {
  isLoading: boolean;
  hasNextPage: boolean;
  onFetchNextPage: () => void;
};

export const useNextPageObserver = ({ isLoading, hasNextPage, onFetchNextPage }: Props) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Handle intersection observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];

      if (entry.isIntersecting && hasNextPage && !isLoading) {
        onFetchNextPage(); // Trigger next page fetch
      }
    },
    [hasNextPage, isLoading, onFetchNextPage]
  );

  useEffect(() => {
    const options = {
      root: null, // viewport
      rootMargin: '20px',
      threshold: 1.0
    };

    const observer = new IntersectionObserver(handleObserver, options);

    // Store the current sentinelRef value in a variable
    const currentSentinel = sentinelRef.current;

    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel); // Use the stored sentinelRef value
      }
    };
  }, [handleObserver]);

  return { sentinelRef };
};
